import type { BillingPeriod, PlanTier } from "../services/billingApi";

/**
 * Client mirror of `server/src/billing/config` — used only to decide what the
 * UI shows (lock icons, disabled handles, which paywall copy to render).
 *
 * The server re-checks every one of these rules on the request itself, so a
 * mismatch here is a cosmetic bug, never a security hole. Keep it in step with
 * `plans.config.ts` and `templates.config.ts`.
 */

export const PLAN_RANK: Record<PlanTier, number> = {
  FREE: 0,
  BASIC: 1,
  PREMIUM: 2,
  PLATINUM: 3,
  ULTRA_PRO: 4,
};

/** True when `tier` meets or exceeds `required`. */
export const meetsTier = (tier: PlanTier, required: PlanTier): boolean =>
  PLAN_RANK[tier] >= PLAN_RANK[required];

// ─── Templates ──────────────────────────────────────────────────────────────

export type TemplateTier = "CLASSIC_LEGACY" | "PREMIUM";

/**
 * The free Classic Legacy design. `default` is included because CVs created
 * before a template was picked carry that key.
 */
export const CLASSIC_LEGACY_TEMPLATE_KEYS = ["classic-old", "default"];

/** Dynamic lookup populated when templates are loaded from the backend */
export const dynamicTemplatePaidStatus: Record<string, boolean> = {};

export const setTemplatePaidStatuses = (
  templates: Array<{ key: string; isPaid?: boolean }>,
): void => {
  templates.forEach((t) => {
    if (typeof t.isPaid === "boolean") {
      dynamicTemplatePaidStatus[t.key.toLowerCase()] = t.isPaid;
    }
  });
};

/** Unknown keys are treated as premium, matching the server's fail-closed rule. */
export const classifyTemplate = (templateKey: string): TemplateTier => {
  const key = (templateKey ?? "").trim().toLowerCase();
  if (!key) {
    return "CLASSIC_LEGACY";
  }
  if (key in dynamicTemplatePaidStatus) {
    return dynamicTemplatePaidStatus[key] ? "PREMIUM" : "CLASSIC_LEGACY";
  }
  if (CLASSIC_LEGACY_TEMPLATE_KEYS.includes(key)) {
    return "CLASSIC_LEGACY";
  }
  return "PREMIUM";
};

/** Minimum plan tier that can export the given design. */
export const requiredTierForTemplate = (templateKey: string): PlanTier =>
  classifyTemplate(templateKey) === "PREMIUM" ? "PREMIUM" : "BASIC";

/** Plans offered in the paywall for a given design, cheapest first. */
export const suggestedTiersForTemplate = (templateKey: string): PlanTier[] =>
  classifyTemplate(templateKey) === "PREMIUM"
    ? ["PREMIUM", "PLATINUM", "ULTRA_PRO"]
    : ["BASIC", "PREMIUM", "PLATINUM"];

// ─── Features ───────────────────────────────────────────────────────────────

export type GatedFeature =
  | "REORDER_SECTIONS"
  | "ATS_ENGINE"
  | "PORTFOLIO"
  | "AI_CAREER_AGENT";

/** Cheapest plan that unlocks each capability. */
export const FEATURE_MINIMUM_TIER: Record<GatedFeature, PlanTier> = {
  REORDER_SECTIONS: "PREMIUM",
  ATS_ENGINE: "PREMIUM",
  PORTFOLIO: "PREMIUM",
  AI_CAREER_AGENT: "PLATINUM",
};

/** Headline shown at the top of the paywall for each capability. */
export const FEATURE_TITLE: Record<GatedFeature, string> = {
  REORDER_SECTIONS: "Unlock custom section layouts",
  ATS_ENGINE: "Unlock ATS compatibility analysis",
  PORTFOLIO: "Unlock your Live Rx Portfolio",
  AI_CAREER_AGENT: "Unlock the AI Career Assistant",
};

export const FEATURE_BLURB: Record<GatedFeature, string> = {
  REORDER_SECTIONS:
    "Drag sections into any order — put Projects above Experience, or lead with Skills.",
  ATS_ENGINE:
    "Real-time ATS scoring, job-description keyword gap analysis and formatting checks.",
  PORTFOLIO:
    "Publish your CV as a hosted web portfolio you can share with a single link.",
  AI_CAREER_AGENT:
    "An interactive career consultant that reviews your CV and coaches your applications.",
};

export const suggestedTiersForFeature = (feature: GatedFeature): PlanTier[] =>
  feature === "AI_CAREER_AGENT"
    ? ["PLATINUM", "ULTRA_PRO"]
    : ["PREMIUM", "PLATINUM", "ULTRA_PRO"];

// ─── Formatting ─────────────────────────────────────────────────────────────

/** `LKR 2,499` / `$14.99` — matches how the pricing schedule renders prices. */
export const formatPrice = (
  amount: number,
  currency: "LKR" | "USD",
): string => {
  if (currency === "LKR") {
    return `LKR ${amount.toLocaleString("en-LK", {
      maximumFractionDigits: 0,
    })}`;
  }
  return `$${amount.toFixed(2)}`;
};

export const formatCredits = (
  credits: number | "UNLIMITED",
  billingPeriod: BillingPeriod = "ONE_TIME",
): string => {
  if (credits === "UNLIMITED") return "Unlimited downloads";

  const noun = `${credits} PDF download${credits === 1 ? "" : "s"}`;
  // Monthly plans grant an allowance per cycle, not a permanent balance.
  return billingPeriod === "MONTHLY" ? `${noun} / month` : noun;
};

/** `/month` for subscriptions, `one-time` for the Basic Pass. */
export const billingPeriodLabel = (billingPeriod: BillingPeriod): string =>
  billingPeriod === "MONTHLY" ? "/month" : "one-time";

/** Longer form for body copy and confirmations. */
export const billingPeriodDescription = (
  billingPeriod: BillingPeriod,
): string =>
  billingPeriod === "MONTHLY"
    ? "Billed monthly until you cancel"
    : "One-time payment · credits never expire";
